import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SelectItem, Message } from 'primeng/api';
import { MessageService } from '@shared/services/message-service';
import { BehaviorSubject, combineLatest, Subject, Subscription } from 'rxjs';
import { ProductService } from '../../../../services/product.service';
import { ActivityBlockingService } from '@shared/services/activity-blocking.service';
import { filter, tap } from 'rxjs/operators';
import { InvCountProductCount } from '../../../../models/inv-count-product-count';
import { BayType } from '@shared/models/bay-type';
import { InventoryService } from '../../../../services/inventory.service';
import { ShiftContextService } from '../../../../services/shift-context.service';
import { ShiftStatus } from '@shared/models/worked-shift';
import { ShiftContext } from '../../../../models/shift-context';
import { saveDebounce } from '@shared/helpers/save-debounce';
import { once } from '@shared/helpers/once';

type RepairLineForm = FormGroup<{
    id: FormControl<number>;
    displayOrder: FormControl<number>;
    productId: FormControl<number>;
    pieces1: FormControl<number>;
}>;

@Component({
    selector: 'app-repair-line-inventory',
    templateUrl: './repair-line-inventory.component.html',
    styleUrls: ['./repair-line-inventory.component.scss']
})
export class RepairLineInventoryComponent implements OnInit, OnDestroy {

    @Input() invCountId: number;
    @Input() set subinventoryId(subinventoryId: number) {
        this.siteSubinventoryId.next(subinventoryId);
    }

    repairLineInventoryForm = new FormGroup({
        product: new FormControl<number>(null, Validators.required),
        pieces: new FormControl<number>(0, Validators.required)
    });

    updatedInvProductCounts: InvCountProductCount[] = [];
    lastUpdatedProductCountId: number;
    products: SelectItem[] = [];
    subscriptions: Subscription[];
    shutdown: Subject<void> = new Subject<void>();
    siteId: string;
    shifts: SelectItem[] = [];
    repairLineInventoryIsLoaded = false;
    repairLineInventoryIsSaved = false;
    siteSubinventoryId = new BehaviorSubject<number>(null);

    context: ShiftContext;

    // begin repair line inventory item grid variables
    repairLineInventoryItems: RepairLineForm[] = [];
    autoSaveFormSubscriptions: Subscription[] = [];
    // end repair line inventory item grid variables

    readonly _repairLineInventoryItemNotCreatedMsg: Message = {
        severity: 'warn',
        summary: 'Could not Create Repair Line Inventory Item',
        detail: 'Fields are required.'
    };

    readonly _repairLineInventoryItemCreatedMsg: Message = {
        severity: 'success',
        summary: 'Repair Line Inventory Item Created',
        detail: ''
    };

    readonly _repairLineInventoryItemDeletedMsg: Message = {
        severity: 'warn',
        summary: 'Repair Line Inventory Item Deleted',
        detail: ''
    };
    constructor(
        private inventoryService: InventoryService,
        private productService: ProductService,
        private shiftContextService: ShiftContextService,
        private messageService: MessageService,
        private activityService: ActivityBlockingService) {
    }

    ngOnInit() {
        const siteObservable = this.shiftContextService.CurrentContext.pipe(tap(context => {
            this.context = context;

            if (context.status !== ShiftStatus.Open) {
                this.repairLineInventoryForm.disable();
                this.repairLineInventoryItems.forEach(item => item.disable({ emitEvent: false }));
            }
            else {
                this.repairLineInventoryForm.enable();
                this.repairLineInventoryItems.forEach(item => item.enable({ emitEvent: false }));
            }
        }));

        const productsObservable = this.productService.ProductsForSubinventory(this.siteSubinventoryId)
            .pipe(tap(products => {
                this.products = products.map(p => ({ value: p.id, label: p.label }));
            }));

        const repairLineInventoryObservable = this.inventoryService.CurrentInvProductCount.pipe(
            filter(pCount => !!pCount),
            tap(pCount => {
                this.repairLineInventoryItems = pCount
                    .filter(x => x.bayType === BayType.RepairLine)
                    .map(item => this.addFormGroup(item));

                if (this.context?.status !== ShiftStatus.Open) {
                    this.repairLineInventoryForm.disable();
                    this.repairLineInventoryItems.forEach(item => item.disable({ emitEvent: false }));
                }
                else {
                    this.repairLineInventoryForm.enable();
                    this.repairLineInventoryItems.forEach(item => item.enable({ emitEvent: false }));
                }

                this.autoSaveFormSubscriptions.forEach(s => s.unsubscribe());
                this.autoSaveFormSubscriptions = this.repairLineInventoryItems.map(item => item.valueChanges
                    .pipe(saveDebounce(this.shutdown))
                    .subscribe(_ => {
                        this.updateRepairLineField(item);
                    }));
            }));

        this.subscriptions = [
            combineLatest([ siteObservable, productsObservable, repairLineInventoryObservable ])
                .pipe(this.activityService.Wait('Repair Lines'))
                .subscribe()
        ];
    }

    ngOnDestroy() {
        this.shutdown.next();
        this.subscriptions.forEach(s => s.unsubscribe());
        this.autoSaveFormSubscriptions.forEach(s => s.unsubscribe());
        this.autoSaveFormSubscriptions = [];
    }

    updateRepairLineField(repairLineInventoryItem: RepairLineForm) {
        const item: InvCountProductCount = {
            id: repairLineInventoryItem.controls.id.value,
            productId: repairLineInventoryItem.controls.productId.value,
            pieces1: repairLineInventoryItem.controls.pieces1.value,
            displayOrder: repairLineInventoryItem.controls.displayOrder.value,
            bayType: BayType.RepairLine,
            invCountId: this.invCountId
        };

        this.inventoryService.updateInvCountProductCount(item).subscribe(_updated => {
            this.messageService.add({
                severity: 'success',
                summary: 'Repair Line Item Updated',
                detail: ''
            });
        });
    }

    saveRLI() {
        if (!this.repairLineInventoryForm.valid) {
            this.repairLineInventoryForm.markAllAsTouched();
            this.messageService.add(this._repairLineInventoryItemNotCreatedMsg);
        } else {
            const newInvCountEntry: InvCountProductCount = {
                id: 0,
                productId: this.repairLineInventoryForm.controls.product.value,
                pieces1: +this.repairLineInventoryForm.controls.pieces.value,
                displayOrder: this.repairLineInventoryItems.length
                    ? this.repairLineInventoryItems[this.repairLineInventoryItems.length - 1].controls.displayOrder.value + 1
                    : 1,
                bayType: BayType.RepairLine,
                invCountId: this.invCountId
            };

            this.inventoryService.createInvCountProductCount(newInvCountEntry).subscribe(created => {
                const form = this.addFormGroup(created);
                this.repairLineInventoryItems = [ ...this.repairLineInventoryItems, form ];

                this.autoSaveFormSubscriptions.push(form.valueChanges
                    .pipe(saveDebounce(this.shutdown))
                    .subscribe(_ => {
                        this.updateRepairLineField(form);
                    }));
                this.repairLineInventoryForm.reset({ product: null, pieces: 0 });
                this.messageService.add(this._repairLineInventoryItemCreatedMsg);
            });
        }
    }

    addFormGroup(item: InvCountProductCount) {
        return new FormGroup({
            id: new FormControl<number>(item.id),
            displayOrder: new FormControl<number>(item.displayOrder),
            productId: new FormControl<number>(item.productId, Validators.required),
            pieces1: new FormControl<number>(item.pieces1, Validators.required)
        });
    }

    deleteRepairLine(repairLineInventoryItem: RepairLineForm) {
        this.inventoryService.deleteInvCountProductCount(repairLineInventoryItem.controls.id.value).subscribe(() => {
            this.repairLineInventoryItems.splice(this.repairLineInventoryItems.indexOf(repairLineInventoryItem), 1);
            this.messageService.add(this._repairLineInventoryItemDeletedMsg);
        });
    }

    rowTrackBy(_i: number, item: RepairLineForm): number {
        return item.controls.id.value;
    }
}
